import { useDispatch } from "react-redux";
import Footer from "../../SharedModule/components/Footer";
import { verifyModuleAccess } from "../../SharedModule/utils/accessUtils";
import { Spinner } from "../../SharedModule/components/Spinner";
import {
  CLIENTS_CONTACTS_ADMIN_ACCESS,
  ROUTE_CLIENTS,
  ROUTE_CLIENTS_CONTACTS,
  ROUTE_CLIENTS_RELATIONSHIP_REVIEW,
} from "../../SharedModule/utils/constants";
import { ForbiddenPage } from "../../SharedModule/pages/ForbiddenPage";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { setActiveTab } from "../../SharedModule/redux/reducers/uiShared.reducer";
import { useEffect, useRef, useState } from "react";
import HeaderClientsModule from "../components/HeaderClientsModule";
import { BiLeftArrowAlt } from "react-icons/bi";
import { NewContactProfile } from "../types/clients-module-types";
import { Select } from "../../SharedModule/components/Select";
import { ClientsService } from "../services/clients";
import { addToast } from "../../SharedModule/redux/reducers/ui.reducer";
import { Calendar } from "../../SharedModule/components/Calendar";
import {
  NEW_CONTACT,
  isValidEmailFormat,
} from "../utils/clientsUtils";
import { useClientNewContact } from "../hooks/useClientNewContact";
import { useContactDropdownHandler } from "../hooks/useContactDropdownHandler";
import { CheckSkeleton } from "../../SharedModule/utils/checkSkeleton";
import { setNewUpdate } from "../redux/reducers/clientContacts.reducer";
import { restoreContactProfile } from "../redux/reducers/clientContactProfile.reducer";
import { resetScrollY } from "../../SharedModule/utils/pagesUtils";

// The API's generic error envelope mixes a human message in with trace/request
// IDs, and which slot holds which differs by environment (see
// ExceptionHandlingMiddleware). Picking a fixed index (e.g. the last one) can
// surface a bare "Request ID: ..." string to the user in production instead
// of anything useful, so filter those out and fall back to a fixed message
// when nothing meaningful is left.
const getFriendlyErrorMessage = (err: any, fallback: string): string => {
  const errors: string[] = err?.data?.errors ?? [];
  const meaningful = errors.find(
    (e) =>
      typeof e === "string" &&
      !/^(Trace|Request) ID:/i.test(e) &&
      !/^An (error|unexpected error)/i.test(e)
  );
  return meaningful || fallback;
};

export const ClientNewContactPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { id } = useParams();

  // If there is no id, go to clients list
  if (!id) {
    navigate(ROUTE_CLIENTS);
  }

  const {
    isLoadingInfo,
    isLoadingDropdownInfo,
    actualClientId,
    userRolesList,
    rolesLoaded,
    dropdownsValues,
  } = useClientNewContact(Number(id));

  useEffect(() => {
    document.title = "The Hub - New Contact Profile";

    dispatch(setActiveTab(ROUTE_CLIENTS_CONTACTS));

    return () => {
      dispatch(restoreContactProfile());
      resetScrollY(globalThis.location.pathname);
    };
  }, []);

  let routeGoBack = ROUTE_CLIENTS_CONTACTS.replace(
    ":id",
    actualClientId ? String(actualClientId) : String(id)
  );

  const [profile, setProfile] = useState<NewContactProfile>(NEW_CONTACT);
  const [isTouched, setIsTouched] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [isEmailTouched, setIsEmailTouched] = useState(false);
  const [isDuplicatedEmail, setIsDuplicatedEmail] = useState(false);
  const [isValidatingEmail, setIsValidatingEmail] = useState(false);
  const [validatedEmail, setValidatedEmail] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // A ref (not state) because it must block a second click synchronously —
  // state set here wouldn't be reflected in `disabled` until the next render,
  // which is exactly the gap a fast double-click slips through.
  const isSubmittingRef = useRef(false);
  const [dateOfBirth, setDateOfBirth] = useState<any>(null);
  const [actualCountry, setActualCountry] = useState<any>(undefined);
  const [actualState, setActualState] = useState<any>(undefined);
  const [actualRoleType, setActualRoleType] = useState<any>(undefined);
  const [actualDiscProfile, setActualDiscProfile] = useState<any>(undefined);
  const [actualSalutation, setActualSalutation] = useState<any>(undefined);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setIsTouched(true);

    if (name === "email") {
      setIsValidEmail(isValidEmailFormat(value));
    }

    setProfile((prevData) => {
      if (!prevData) return prevData;
      return {
        ...prevData,
        [name]: value,
      };
    });
  };

  const handleChangeDropdown = useContactDropdownHandler(
    profile,
    setProfile,
    setIsTouched,
    {
      setActualCountry,
      setActualState,
      setActualRoleType,
      setActualDiscProfile,
      setActualSalutation,
    }
  );

  // Authoritative duplicate-email check. Returns the result directly instead of
  // relying on state, since state set here is not visible to a caller's own
  // closure until the next render (see `create` below for why that matters).
  const validateEmail = async (
    value: string
  ): Promise<{ duplicated: boolean; data?: any }> => {
    setIsValidatingEmail(true);
    try {
      const resp = await ClientsService.getClientContactProfileByEmail(value);
      if (resp.success) {
        dispatch(
          addToast({
            mode: "warning",
            message:
              "This email belongs to an existing contact. You can reuse it here.",
          })
        );
        setProfile(resp.data);
        let dateOfBirthDB = resp.data.dateOfBirth
          ? `${resp.data.dateOfBirth.split("T")[0]}T03:00:00.000`
          : null;
        setDateOfBirth(dateOfBirthDB ? new Date(dateOfBirthDB) : null);
        setActualCountry(
          dropdownsValues?.countries.find(
            (country) => country.value === resp.data?.countryId
          )
        );
        setActualState(
          dropdownsValues?.states.find(
            (state) => state.value === resp.data?.stateId
          )
        );
        let types =
          dropdownsValues?.contactRoleTypes.filter((role) =>
            resp.data?.contactRoleTypeId.includes(role.value)
          ) || [];
        setActualRoleType(types);
        setActualDiscProfile(
          dropdownsValues?.discProfiles.find(
            (disc) => disc.value === resp.data?.discProfileOptionId
          )
        );
        setActualSalutation(
          dropdownsValues?.salutations.find(
            (salutation) => salutation.value === resp.data?.salutationId
          )
        );
        setIsDuplicatedEmail(true);
        setValidatedEmail(value);
        return { duplicated: true, data: resp.data };
      } else {
        setDateOfBirth(null);
        setActualCountry(undefined);
        setActualState(undefined);
        setActualRoleType(undefined);
        setActualDiscProfile(undefined);
        setActualSalutation(undefined);
        setProfile({ ...NEW_CONTACT, email: value });
        setIsDuplicatedEmail(false);
        setValidatedEmail(value);
        return { duplicated: false };
      }
    } catch {
      dispatch(
        addToast({
          mode: "error",
          message: "Something went wrong validating this email",
        })
      );
      // Leave validatedEmail unset so a later submit attempt re-checks
      // instead of assuming the email is safe.
      return { duplicated: false };
    } finally {
      setIsValidatingEmail(false);
    }
  };

  const handleBlur = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setIsDuplicatedEmail(false);
    setIsEmailTouched(true);
    const { value } = e.target;
    if (isValidEmail) {
      await validateEmail(value);
    }
  };

  const create = async () => {
    // Blocks a second Save click while one is already in flight. Checked via
    // a ref (not the `isSubmitting` state) so a fast double-click can't slip
    // through before React re-renders the disabled button — the same class
    // of race that let the email-duplicate check be skipped.
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      setIsSubmitted(true);

      if (!isValidEmail) {
        return;
      }

      // The onBlur check may not have finished (e.g. the user clicked Save
      // right after typing/pasting the email). Re-validate here so a duplicate
      // is never missed just because of that race, instead of trusting
      // possibly-stale `isDuplicatedEmail` state.
      let duplicated = isDuplicatedEmail;
      if (profile.email !== validatedEmail) {
        const result = await validateEmail(profile.email);
        duplicated = result.duplicated;
        if (duplicated) {
          dispatch(
            addToast({
              mode: "warning",
              message:
                "This contact already exists. Review the pre-filled details and click Save again.",
            })
          );
          return;
        }
      }

      const cloned = {
        ...profile,
        clientIds: profile.clientIds.includes(Number(id))
          ? profile.clientIds
          : [...profile.clientIds, Number(id)],
      };

      // Remove properties with null values
      const cleaned = Object.fromEntries(
        Object.entries(cloned).filter(([_, value]) => value !== null)
      );

      if (duplicated && cloned.id) {
        await ClientsService.updateContactProfile(cleaned, cloned.id)
          .then((res) => {
            dispatch(
              addToast({
                mode: "success",
                message: res.message,
              })
            );
            setIsTouched(false);
            dispatch(setNewUpdate(true));
            dispatch(restoreContactProfile());
            navigate(routeGoBack, {
              state: {
                from:
                  location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
                    ? ROUTE_CLIENTS_RELATIONSHIP_REVIEW
                    : "",
              },
            });
          })
          .catch(() => {
            dispatch(
              addToast({
                mode: "error",
                message: "Something went wrong updating this contact",
              })
            );
          });
      } else {
        await ClientsService.addContact(cleaned)
          .then((res) => {
            dispatch(
              addToast({
                mode: "success",
                message: res.message,
              })
            );
            setIsTouched(false);
            dispatch(setNewUpdate(true));
            dispatch(restoreContactProfile());
            navigate(routeGoBack, {
              state: {
                from:
                  location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
                    ? ROUTE_CLIENTS_RELATIONSHIP_REVIEW
                    : "",
              },
            });
          })
          .catch((err) => {
            dispatch(
              addToast({
                mode: "error",
                message: getFriendlyErrorMessage(
                  err,
                  "Something went wrong creating this contact"
                ),
              })
            );
          });
      }
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const renderEmailField = () => (
    <div className="d-flex p-0 mb-3">
      <CheckSkeleton
        classes="flex-column w-50 me-5"
        isLoading={!profile}
        style={{ height: "38px" }}
      >
        <div className="flex-column w-50 me-5">
          <label className="col-form-label d-block">
            Email
            <span
              style={{
                color:
                  profile?.email.length === 0 && isSubmitted
                    ? "red"
                    : "black",
              }}
            >
              {" "}
              *
            </span>
            <input
              type="text"
              className={`form-control form-control-sm common-input ${
                (profile?.email.length === 0 && isSubmitted) ||
                (!isValidEmail && isEmailTouched)
                  ? "border-error"
                  : ""
              }`}
              value={profile?.email}
              name="email"
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isValidatingEmail}
            />
          </label>
          {!isValidEmail && isEmailTouched && (
            <span className="helper-title-error">
              Invalid email format
            </span>
          )}
          {isDuplicatedEmail && (
            <span className="helper-title">
              This contact already exists. You can review their
              details and assign them to this client if needed.
            </span>
          )}
        </div>
      </CheckSkeleton>
      <div className="flex-column w-50 ms-5"></div>
    </div>
  );

  const renderNameFields = () => (
    <div className="d-flex p-0 mb-3">
      <CheckSkeleton
        classes="flex-column w-50 me-5"
        isLoading={!profile || isValidatingEmail}
        style={{ height: "38px" }}
      >
        <div className="flex-column w-50 me-5">
          <label className="col-form-label d-block">
            First Name
            <span
              style={{
                color:
                  profile?.firstName.length === 0 && isSubmitted
                    ? "red"
                    : "black",
              }}
            >
              {" "}
              *
            </span>
            <input
              type="text"
              className={`form-control form-control-sm common-input ${
                profile?.firstName.length === 0 && isSubmitted
                  ? "border-error"
                  : ""
              }`}
              value={profile?.firstName}
              name="firstName"
              onChange={handleChange}
              disabled={!isValidEmail}
            />
          </label>
        </div>
      </CheckSkeleton>
      <CheckSkeleton
        classes="flex-column w-50 ms-5"
        isLoading={!profile || isValidatingEmail}
        style={{ height: "38px" }}
      >
        <div className="flex-column w-50 ms-5">
          <label className="col-form-label d-block">
            Last Name
            <span
              style={{
                color:
                  profile?.lastName.length === 0 && isSubmitted
                    ? "red"
                    : "black",
              }}
            >
              {" "}
              *
            </span>
            <input
              type="text"
              className={`form-control form-control-sm common-input ${
                profile?.lastName.length === 0 && isSubmitted
                  ? "border-error"
                  : ""
              }`}
              value={profile?.lastName}
              name="lastName"
              onChange={handleChange}
              disabled={!isValidEmail}
            />
          </label>
        </div>
      </CheckSkeleton>
    </div>
  );

  const renderContactDetailsFields = () => (
    <>
      <div className="d-flex p-0 mb-3">
        <CheckSkeleton
          classes="flex-column w-50 me-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 me-5">
            <label className="col-form-label d-block">
              Salutation
              <Select
                placeholder="Please Select"
                className="common-input"
                options={dropdownsValues?.salutations}
                value={actualSalutation}
                onChange={(salutation) =>
                  handleChangeDropdown("salutationId", salutation)
                }
                isDisabled={isLoadingDropdownInfo || !isValidEmail}
              />
            </label>
          </div>
        </CheckSkeleton>
        <CheckSkeleton
          classes="flex-column w-50 ms-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 ms-5">
            <label className="col-form-label d-block">
              Nickname{" "}
              <input
                type="text"
                className="form-control form-control-sm common-input"
                value={profile?.nickname}
                name="nickname"
                onChange={handleChange}
                disabled={!isValidEmail}
              />
            </label>
          </div>
        </CheckSkeleton>
      </div>

      <div className="d-flex p-0 mb-3">
        <CheckSkeleton
          classes="flex-column w-50 me-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 me-5">
            <label className="col-form-label d-block">
              Job Title{" "}
              <input
                type="text"
                className="form-control form-control-sm common-input"
                value={profile?.jobTitle}
                name="jobTitle"
                onChange={handleChange}
                disabled={!isValidEmail}
              />
            </label>
          </div>
        </CheckSkeleton>
        <CheckSkeleton
          classes="flex-column w-50 ms-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 ms-5">
            <label className="col-form-label d-block">
              Department{" "}
              <input
                type="text"
                className="form-control form-control-sm common-input"
                value={profile?.department}
                name="department"
                onChange={handleChange}
                disabled={!isValidEmail}
              />
            </label>
          </div>
        </CheckSkeleton>
      </div>

      <div className="d-flex p-0 mb-3">
        <CheckSkeleton
          classes="flex-column w-50 me-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 me-5">
            <label className="col-form-label d-block">
              Contact Role
              <span
                style={{
                  color:
                    profile?.contactRoleTypeId.length === 0 &&
                    isSubmitted
                      ? "red"
                      : "black",
                }}
              >
                {" "}
                *
              </span>
              <Select
                placeholder="Please Select"
                isMulti
                className="common-input"
                options={dropdownsValues?.contactRoleTypes}
                value={actualRoleType}
                onChange={(role) =>
                  handleChangeDropdown("contactRoleTypeId", role)
                }
                isDisabled={isLoadingDropdownInfo || !isValidEmail}
              />
            </label>
          </div>
        </CheckSkeleton>
        <CheckSkeleton
          classes="flex-column w-50 ms-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 ms-5">
            <label className="col-form-label d-block">
              DISC Profile
              <Select
                placeholder="Please Select"
                className="common-input"
                options={dropdownsValues?.discProfiles}
                value={actualDiscProfile}
                onChange={(disc) =>
                  handleChangeDropdown("discProfileOptionId", disc)
                }
                isDisabled={isLoadingDropdownInfo || !isValidEmail}
              />
            </label>
          </div>
        </CheckSkeleton>
      </div>
    </>
  );

  const renderAddressFields = () => (
    <>
      <div className="d-flex p-0 mb-3">
        <CheckSkeleton
          classes="flex-column w-50 me-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 me-5">
            <label className="col-form-label d-block">
              Street Address{" "}
              <input
                type="text"
                className="form-control form-control-sm common-input"
                value={profile?.streetAddress}
                name="streetAddress"
                onChange={handleChange}
                disabled={!isValidEmail}
              />
            </label>
          </div>
        </CheckSkeleton>
        <CheckSkeleton
          classes="flex-column w-50 ms-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 ms-5">
            <label className="col-form-label d-block">
              Street Address 2{" "}
              <input
                type="text"
                className="form-control form-control-sm common-input"
                value={profile?.streetAddress2}
                name="streetAddress2"
                onChange={handleChange}
                disabled={!isValidEmail}
              />
            </label>
          </div>
        </CheckSkeleton>
      </div>

      <div className="d-flex p-0 mb-3">
        <CheckSkeleton
          classes="flex-column w-50 me-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 me-5">
            <label className="col-form-label d-block">
              City{" "}
              <input
                type="text"
                className="form-control form-control-sm common-input"
                value={profile?.city}
                name="city"
                onChange={handleChange}
                disabled={!isValidEmail}
              />
            </label>
          </div>
        </CheckSkeleton>
        <CheckSkeleton
          classes="flex-column w-50 ms-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 ms-5">
            <label className="col-form-label d-block">
              State
              <Select
                placeholder="Please Select"
                className="common-input"
                options={dropdownsValues?.states}
                value={actualState}
                onChange={(state) =>
                  handleChangeDropdown("stateId", state)
                }
                isDisabled={isLoadingDropdownInfo || !isValidEmail}
              />
            </label>
          </div>
        </CheckSkeleton>
      </div>

      <div className="d-flex p-0 mb-3">
        <CheckSkeleton
          classes="flex-column w-50 me-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 me-5">
            <label className="col-form-label d-block">
              Postal code{" "}
              <input
                type="text"
                className="form-control form-control-sm common-input"
                value={profile?.postalCode}
                name="postalCode"
                onChange={handleChange}
                disabled={!isValidEmail}
              />
            </label>
          </div>
        </CheckSkeleton>
        <CheckSkeleton
          classes="flex-column w-50 ms-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 ms-5">
            <label className="col-form-label d-block">
              Country
              <span
                style={{
                  color:
                    !profile?.countryId && isSubmitted
                      ? "red"
                      : "black",
                }}
              >
                {" "}
                *
              </span>
              <Select
                placeholder="Please Select"
                className={`common-input ${
                  !profile?.countryId && isSubmitted
                    ? "select-border-error"
                    : ""
                }`}
                options={dropdownsValues?.countries}
                value={actualCountry}
                onChange={(country) =>
                  handleChangeDropdown("countryId", country)
                }
                isDisabled={isLoadingDropdownInfo || !isValidEmail}
              />
            </label>
          </div>
        </CheckSkeleton>
      </div>
    </>
  );

  const renderPersonalInfoFields = () => (
    <>
      <div className="d-flex p-0 mb-3">
        <CheckSkeleton
          classes="flex-column w-50 me-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 me-5">
            <label className="col-form-label d-block">
              Phone Number{" "}
              <input
                type="text"
                className="form-control form-control-sm common-input"
                value={profile?.phoneNumber}
                name="phoneNumber"
                onChange={handleChange}
                disabled={!isValidEmail}
              />
            </label>
          </div>
        </CheckSkeleton>
        <CheckSkeleton
          classes="flex-column w-50 ms-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 ms-5">
            <label className="col-form-label d-block">
              Mobile Phone Number{" "}
              <input
                type="text"
                className="form-control form-control-sm common-input"
                value={profile?.mobilePhoneNumber}
                name="mobilePhoneNumber"
                onChange={handleChange}
                disabled={!isValidEmail}
              />
            </label>
          </div>
        </CheckSkeleton>
      </div>

      <div className="d-flex p-0 mb-3">
        <CheckSkeleton
          classes="flex-column w-50 me-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 me-5">
            <label className="col-form-label d-block">
              Date of Birth
              <Calendar
                date={dateOfBirth}
                initNull={true}
                onChange={(date: string) => {
                  setIsTouched(true);
                  setDateOfBirth(new Date(date));
                  setProfile({
                    ...profile,
                    dateOfBirth: `${date.split("T")[0]}T03:00:00.000`,
                  });
                }}
                isDisabled={!isValidEmail}
              />
            </label>
          </div>
        </CheckSkeleton>
        <CheckSkeleton
          classes="flex-column w-50 ms-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 ms-5">
            <label className="col-form-label d-block">
              Hobbies{" "}
              <input
                type="text"
                className="form-control form-control-sm common-input"
                value={profile?.hobbies}
                name="hobbies"
                onChange={handleChange}
                disabled={!isValidEmail}
              />
            </label>
          </div>
        </CheckSkeleton>
      </div>

      <div className="d-flex p-0 mb-3">
        <CheckSkeleton
          classes="flex-column w-50 me-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 me-5">
            <label className="col-form-label d-block">
              Spouse First Name{" "}
              <input
                type="text"
                className="form-control form-control-sm common-input"
                value={profile?.spouseFirstName}
                name="spouseFirstName"
                onChange={handleChange}
                disabled={!isValidEmail}
              />
            </label>
          </div>
        </CheckSkeleton>
        <CheckSkeleton
          classes="flex-column w-50 ms-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 ms-5">
            <label className="col-form-label d-block">
              Spouse Last Name{" "}
              <input
                type="text"
                className="form-control form-control-sm common-input"
                value={profile?.spouseLastName}
                name="spouseLastName"
                onChange={handleChange}
                disabled={!isValidEmail}
              />
            </label>
          </div>
        </CheckSkeleton>
      </div>

      <div className="d-flex p-0 mb-3">
        <CheckSkeleton
          classes="flex-column w-50 me-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 me-5">
            <label className="col-form-label d-block">
              Assistant First Name{" "}
              <input
                type="text"
                className="form-control form-control-sm common-input"
                value={profile?.assistantFirstName}
                name="assistantFirstName"
                onChange={handleChange}
                disabled={!isValidEmail}
              />
            </label>
          </div>
        </CheckSkeleton>
        <CheckSkeleton
          classes="flex-column w-50 ms-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 ms-5">
            <label className="col-form-label d-block">
              Assistant Last Name{" "}
              <input
                type="text"
                className="form-control form-control-sm common-input"
                value={profile?.assistantLastName}
                name="assistantLastName"
                onChange={handleChange}
                disabled={!isValidEmail}
              />
            </label>
          </div>
        </CheckSkeleton>
      </div>

      <div className="d-flex p-0 mb-3">
        <CheckSkeleton
          classes="flex-column w-50 me-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 me-5">
            <label className="col-form-label d-block">
              Assistant Phone Number{" "}
              <input
                type="text"
                className="form-control form-control-sm common-input"
                value={profile?.assistantPhoneNumber}
                name="assistantPhoneNumber"
                onChange={handleChange}
                disabled={!isValidEmail}
              />
            </label>
          </div>
        </CheckSkeleton>
        <CheckSkeleton
          classes="flex-column w-50 ms-5"
          isLoading={!profile || isValidatingEmail}
          style={{ height: "38px" }}
        >
          <div className="flex-column w-50 ms-5">
            <label className="col-form-label d-block">
              Assistant Email{" "}
              <input
                type="text"
                className="form-control form-control-sm common-input"
                value={profile?.assistantEmail}
                name="assistantEmail"
                onChange={handleChange}
                disabled={!isValidEmail}
              />
            </label>
            {!isValidEmailFormat(profile?.assistantEmail) &&
              profile?.assistantEmail?.length > 0 && (
                <span className="helper-title-error">
                  Invalid email format
                </span>
              )}
          </div>
        </CheckSkeleton>
      </div>
    </>
  );

  return (
    <div className="content">
      <HeaderClientsModule
        showBackButton={false}
        isLoadingPage={isLoadingInfo || !rolesLoaded}
      />

      {(isLoadingInfo || !rolesLoaded) && (
        <Spinner
          style={{
            marginLeft: "50%",
            marginTop: "5%",
          }}
        />
      )}

      {!isLoadingInfo &&
        rolesLoaded &&
        !verifyModuleAccess(
          true,
          CLIENTS_CONTACTS_ADMIN_ACCESS,
          userRolesList
        ) && <ForbiddenPage />}

      {!isLoadingInfo &&
        rolesLoaded &&
        verifyModuleAccess(
          true,
          CLIENTS_CONTACTS_ADMIN_ACCESS,
          userRolesList
        ) && (
          <>
            <Link
              className="back-button-container text-decoration-none mt-4"
              to={routeGoBack}
              state={{
                from:
                  location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
                    ? ROUTE_CLIENTS_RELATIONSHIP_REVIEW
                    : "",
              }}
            >
              <BiLeftArrowAlt className="back-button-arrow" />
              <span className="back-button-text">Back to Contacts</span>
            </Link>

            {!isLoadingInfo && (
              <form className="card entry-hour-box-background px-4 py-2 my-3">
                <div
                  className="d-flex justify-content-between"
                  style={{ margin: "1% 8%" }}
                >
                  <h3 style={{ marginTop: "0.5rem" }}>
                    Contact Personal Information
                  </h3>
                </div>

                <div className="row" style={{ margin: "0 8%" }}>
                  {renderEmailField()}
                  {renderNameFields()}
                  {renderContactDetailsFields()}
                  {renderAddressFields()}
                  {renderPersonalInfoFields()}

                  <div className="d-flex p-0 my-3">
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={() => create()}
                      disabled={
                        !profile ||
                        !isTouched ||
                        profile?.firstName.length === 0 ||
                        profile?.lastName.length === 0 ||
                        profile?.email.length === 0 ||
                        !profile?.countryId ||
                        !isValidEmail ||
                        isValidatingEmail ||
                        isSubmitting ||
                        (!isValidEmailFormat(profile?.assistantEmail) &&
                          profile?.assistantEmail?.length > 0) ||
                        profile?.contactRoleTypeId.length === 0
                      }
                    >
                      Save
                    </button>
                  </div>
                </div>
              </form>
            )}
          </>
        )}
      <Footer />
    </div>
  );
};
